import { clsx } from "clsx";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "warning" | "excel";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: [
    "bg-[var(--color-primary)] text-[var(--color-text-inverse)]",
    "shadow-hard-red hover:bg-[var(--color-primary-hover)]",
    "active:translate-x-[3px] active:translate-y-[3px] active:shadow-none",
    // The press effect moves the button out from under the pointer between
    // mousedown and mouseup, which swallows the click (user has to click twice).
    // While pressed, extend the hit area by the travel distance so the pointer
    // stays inside the button for the whole press.
    "relative active:before:content-[''] active:before:absolute active:before:-inset-[3px]",
  ].join(" "),
  secondary: [
    "bg-[var(--color-bg-surface)]",
    "border border-[var(--color-border-strong)] text-[var(--color-text-primary)]",
    "hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
  ].join(" "),
  ghost: [
    "border border-[var(--color-border-strong)] text-[var(--color-text-secondary)]",
    "hover:border-[var(--color-text-primary)] hover:text-[var(--color-text-primary)]",
  ].join(" "),
  danger: [
    "border border-[var(--color-primary)] text-[var(--color-text-primary)]",
    "hover:bg-[var(--color-primary-dim)]",
  ].join(" "),
  warning: [
    "bg-[var(--color-bg-surface)]",
    "border border-[var(--color-border-strong)] text-[var(--color-text-secondary)]",
    "hover:border-[var(--color-text-primary)] hover:text-[var(--color-text-primary)]",
  ].join(" "),
  // Microsoft Excel green — spreadsheet-like actions („Licz jako arkusz").
  excel: [
    "bg-[#217346] border border-[#217346] text-white",
    "hover:bg-[#185c37] hover:border-[#185c37]",
  ].join(" "),
};

const sizes: Record<Size, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-5 py-2.5 text-xs",
  lg: "px-7 py-3.5 text-sm",
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={clsx(
        "inline-flex items-center justify-center gap-2",
        "font-mono font-medium uppercase tracking-wider",
        "transition-all duration-100",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none",
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
}
